import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { addDays } from 'date-fns'

import { RootState } from '../store'

import {
  CalendarEvent,
  RepeatingEvent,
  Workspace,
  WorkspaceInvitation,
} from './workspaceSlice.types'
import { toDateString } from '../../lib/shared/calendar.utils'

interface WorkspaceState {
  workspaces: Workspace[]
  hasFetchedWorkspaces: boolean
  currentWorkspace: string | null
  repeatingEvents: Record<string, RepeatingEvent[]>
  calendarEvents: Record<string, CalendarEvent[]>
  calendarDateRange: {
    startDate: string
    endDate: string
  }
  invitations: Record<string, WorkspaceInvitation[]>
}

const initialState: WorkspaceState = {
  currentWorkspace: null,
  workspaces: [],
  hasFetchedWorkspaces: false,
  calendarEvents: {},
  repeatingEvents: {},
  calendarDateRange: {
    startDate: toDateString(new Date()),
    endDate: toDateString(addDays(new Date(), 365)),
  },
  invitations: {},
}

const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    setWorkspaces: (state, action: PayloadAction<Workspace[]>) => {
      state.workspaces = action.payload
      state.hasFetchedWorkspaces = true

      const firstItem = action.payload[0]?.id ?? null

      if (!state.currentWorkspace) {
        state.currentWorkspace = firstItem
      } else {
        const ids = action.payload.map((b) => b.id)
        if (!ids.includes(state.currentWorkspace)) {
          state.currentWorkspace = firstItem
        }
      }
    },
    setCurrentWorkspace: (state, action: PayloadAction<{ id: string }>) => {
      state.currentWorkspace = action.payload.id
    },
    addWorkspace: (state, action: PayloadAction<Workspace>) => {
      if (!state.workspaces.find((b) => b.id === action.payload.id)) {
        state.workspaces.push(action.payload)
      }
      state.currentWorkspace = action.payload.id
    },
    addCalendarEvents: (
      state,
      action: PayloadAction<{ workspaceId: string; events: CalendarEvent[] }>,
    ) => {
      // TODO - allow for adding incrementally
      state.calendarEvents[action.payload.workspaceId] = action.payload.events
    },
    addRepeatingEvents: (
      state,
      action: PayloadAction<{ workspaceId: string; events: RepeatingEvent[] }>,
    ) => {
      state.repeatingEvents[action.payload.workspaceId] = action.payload.events
    },
    setInvitations: (
      state,
      action: PayloadAction<{
        workspaceId: string
        invitations: WorkspaceInvitation[]
      }>,
    ) => {
      state.invitations[action.payload.workspaceId] = action.payload.invitations
    },
    setCalendarDateRange: (
      state,
      action: PayloadAction<{ startDate: string; endDate: string }>,
    ) => {
      state.calendarDateRange = action.payload
    },
  },
})

export const {
  setWorkspaces,
  setCurrentWorkspace,
  addWorkspace,
  addCalendarEvents,
  addRepeatingEvents,
  setCalendarDateRange,
  setInvitations,
} = workspaceSlice.actions

export const selectCurrentWorkspace = (state: RootState) =>
  state.workspace.currentWorkspace

export const selectHasFetchedWorkspaces = (state: RootState) =>
  state.workspace.hasFetchedWorkspaces

export const selectWorkspaces = (state: RootState) => state.workspace.workspaces

export const selectCalendarEvents = (state: RootState) =>
  state.workspace.calendarEvents

export const selectRepeatingEvents = (state: RootState) =>
  state.workspace.repeatingEvents

export const selectCalendarDateRange = (state: RootState) =>
  state.workspace.calendarDateRange

export const selectInvitations = (state: RootState) =>
  state.workspace.invitations

export default workspaceSlice.reducer
